import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Trash2, Plus, Theater } from 'lucide-react';

export default function StagePanel({
  stages,
  setStages,
  selectedStageId,
  setSelectedStageId,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: 'Színpad', widthM: 12, heightM: 6 });

  const addStage = () => {
    if (!draft.name) return;
    const id = `stage-${Date.now()}`;
    setStages((prev) => [
      ...prev,
      {
        id,
        name: draft.name,
        widthM: parseFloat(draft.widthM) || 0,
        heightM: parseFloat(draft.heightM) || 0,
        widthPx: 240,
        heightPx: 140,
        x: 950,
        y: 700,
      },
    ]);
    setSelectedStageId(id);
    setDraft({ name: 'Színpad', widthM: 12, heightM: 6 });
    setOpen(false);
  };

  // A méret független a térképen rajzolt mérettől
  const updateStage = (id, patch) => {
    setStages((prev) => prev.map((s) => (s.id !== id ? s : { ...s, ...patch })));
  };

  const removeStage = (id) => {
    setStages((prev) => prev.filter((s) => s.id !== id));
    if (selectedStageId === id) setSelectedStageId(null);
  };

  return (
    <Card className="p-4 bg-white border-stone-200" data-testid="stages-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold tracking-wide text-stone-800 uppercase">Színpadok</h3>
          <p className="text-xs text-stone-500 mt-0.5">{stages.length} db</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="default" data-testid="add-stage-btn" className="h-8 px-3 bg-slate-700 hover:bg-slate-800 text-white">
              <Plus className="w-3.5 h-3.5 mr-1" /> Új színpad
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-stage-dialog">
            <DialogHeader>
              <DialogTitle>Színpad hozzáadása</DialogTitle>
              <DialogDescription>A színpad nem ad fő-számot a kapacitáshoz, csak vizuális jelölés a térképen.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-xs">Név</Label>
                <Input
                  data-testid="stage-name-input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="pl. Színpad / Küzdőtér"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Szélesség (m)</Label>
                  <Input
                    data-testid="stage-width-input"
                    type="number"
                    min="1"
                    value={draft.widthM}
                    onChange={(e) => setDraft({ ...draft, widthM: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Magasság (m)</Label>
                  <Input
                    data-testid="stage-height-input"
                    type="number"
                    min="1"
                    value={draft.heightM}
                    onChange={(e) => setDraft({ ...draft, heightM: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Mégse</Button>
              <Button onClick={addStage} data-testid="save-stage-btn">Hozzáadás</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {stages.length === 0 ? (
        <div className="text-xs text-stone-500 text-center py-6 border border-dashed border-stone-300 rounded-md">
          <Theater className="w-5 h-5 mx-auto mb-1.5 text-stone-400" />
          Nincs még színpad. <span className="text-slate-700 font-semibold">Színpad rajz</span> módban kijelölheted a térképen.
        </div>
      ) : (
        <div className="space-y-2">
          {stages.map((s) => (
            <div
              key={s.id}
              className={`p-2.5 rounded-md border transition-colors ${
                selectedStageId === s.id ? 'border-slate-700 bg-slate-50' : 'border-stone-200 hover:bg-stone-50'
              }`}
              onClick={() => setSelectedStageId(s.id)}
              data-testid={`stage-card-${s.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Input
                  type="text"
                  className="h-7 text-xs font-bold flex-1 mr-2"
                  value={s.name}
                  onChange={(e) => updateStage(s.id, { name: e.target.value })}
                  data-testid={`stage-name-${s.id}`}
                />
                <button
                  onClick={(e) => { e.stopPropagation(); removeStage(s.id); }}
                  className="text-stone-400 hover:text-red-600 transition-colors"
                  data-testid={`remove-stage-${s.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <Label className="text-[10px] text-stone-500">szél (m)</Label>
                  <Input
                    type="number"
                    className="h-7 text-xs"
                    value={s.widthM}
                    onChange={(e) => updateStage(s.id, { widthM: parseFloat(e.target.value) || 0 })}
                    data-testid={`stage-widthm-${s.id}`}
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-stone-500">mag (m)</Label>
                  <Input
                    type="number"
                    className="h-7 text-xs"
                    value={s.heightM}
                    onChange={(e) => updateStage(s.id, { heightM: parseFloat(e.target.value) || 0 })}
                    data-testid={`stage-heightm-${s.id}`}
                  />
                </div>
              </div>
              <div className="mt-2 text-[10px] text-stone-500">
                {s.widthM}×{s.heightM} m — nem számít a kapacitásba
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
